<template>
  <div class="dashboard pb-10 space-y-6">
    <dashboard-header
      :user="user"
      :user-status-label="userStatusLabel"
      :user-greeting="userGreeting"
      :quick-ranges="quickRanges"
      :active-range-id="activeQuickRangeId"
      :date-range="dateRange"
      :max-date="todayFormatted"
      @select-range="applyQuickRange"
      @show-full="showFull"
      @update:date-range="dateRange = $event"
    />

    <page-content>
      <section class="dashboard-main">
        <section class="dashboard-space dashboard-space--overview">
          <header class="dashboard-space__header">
            <div class="dashboard-space__heading">
              <h2 class="dashboard-space__title">L'essentiel, en un coup d'oeil</h2>
              <span v-if="periodLabel" class="dashboard-period-pill">
                <i class="fas fa-calendar-alt"></i>
                <span>Période: {{ periodLabel }}</span>
              </span>
            </div>
            <button
              type="button"
              class="dashboard-primary-button"
              @click="openMonthlyTracking"
            >
              Suivi mois par mois
            </button>
          </header>

          <div class="relative">
            <dashboard-row-toggle
              :collapsed="collapsedLines.overview"
              @toggle="toggleLine('overview')"
            />

            <div class="dashboard-overview-grid dashboard-overview-grid--top">
              <dashboard-downloads-card
                :collapsed="collapsedLines.overview"
                :totals="employeeActivityTotals"
                :daily-series="dashboardEmployeeActivityDailySeries"
                :loading-graphs="loadingGraphs"
                :has-domain-filter="!!(domain && domain.uuid)"
                :format-number="formatNumber"
                @toggle="toggleLine('overview')"
              />

              <dashboard-logins-card
                :collapsed="collapsedLines.overview"
                :totals="employeeActivityTotals"
                :daily-series="dashboardEmployeeActivityDailySeries"
                :loading-graphs="loadingGraphs"
                :has-domain-filter="!!(domain && domain.uuid)"
                :format-number="formatNumber"
                @toggle="toggleLine('overview')"
              />
            </div>
          </div>

          <div class="relative">
            <dashboard-row-toggle
              :collapsed="collapsedLines.insights"
              @toggle="toggleLine('insights')"
            />

            <div class="dashboard-overview-grid dashboard-overview-grid--bottom">
              <dashboard-reads-card
                :collapsed="collapsedLines.insights"
                :top-news="topNews"
                :top-faqs="topFaqs"
                :counters="counters"
                :daily-series="dashboardReadTrackingDailySeries"
                :read-totals="readTrackingTotals"
                :loading-read-tracking="loadingReadTracking"
                :loading-top-news="loadingTopNews"
                :loading-popularities="loadingPopularities"
                :format-number="formatNumber"
                @toggle="toggleLine('insights')"
              />

              <dashboard-top-keywords-card
                :collapsed="collapsedLines.insights"
                :loading-popularities="loadingPopularities"
                :top-searches="topSearches"
                :total-searches="totalSearches"
                :keyword-alerts="keywordAlerts"
                :selected-alert="selectedAlertKeyword"
                :is-exporting="isLoadingExport"
                :format-number="formatNumber"
                :period-label="periodLabel"
                @toggle="toggleLine('insights')"
                @export="downloadSearchExport"
                @toggle-alert="toggleAlertKeyword"
              />

              <dashboard-active-surveys-card
                v-if="shouldDisplayActiveSurveysCard"
                class="dashboard-overview-grid__full"
                :collapsed="collapsedLines.insights"
                card-class="dashboard-overview-card"
                :active-surveys="activeSurveys"
                :total-active="activeSurveysCount"
                :loading-active="loadingActiveSurveys"
                :last-ended-survey="lastEndedSurvey"
                :downloading-stats="isDownloadingLastSurveyStats"
                :format-number="formatNumber"
                :workforce-size="mockEmployeeTargets.totalEmployees"
                @toggle="toggleLine('insights')"
                @download-stats="downloadLastSurveyStats"
              />
            </div>
          </div>
        </section>

        <section class="dashboard-space dashboard-space--actions">
          <header class="dashboard-space__header">
            <div class="dashboard-space__heading">
              <h2 class="dashboard-space__title">Les contenus à publier</h2>
            </div>
            <div class="dashboard-space__commands">
              <button
                type="button"
                class="dashboard-content-action dashboard-content-action--article"
                :disabled="!hasNewsEditionRights || isOpeningArticleModal"
                @click="createArticle"
              >
                <i v-if="isOpeningArticleModal" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                <i v-else class="fas fa-plus-circle" aria-hidden="true"></i>
                Créer une actualité
              </button>
              <button
                type="button"
                class="dashboard-content-action dashboard-content-action--survey"
                :disabled="!hasSurveyEditionRights || isOpeningSurveyModal"
                @click="createSurvey"
              >
                <i v-if="isOpeningSurveyModal" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                <i v-else class="fas fa-plus-circle" aria-hidden="true"></i>
                Créer un sondage
              </button>
            </div>
          </header>

          <div class="dashboard-actions-grid">
            <div class="relative">
              <dashboard-row-toggle
                :collapsed="collapsedLines.news"
                @toggle="toggleLine('news')"
              />

              <dashboard-pending-news-card
                :collapsed="collapsedLines.news"
                title-override="Actualités à publier"
                card-class="dashboard-action-card"
                :loading-pending-news="loadingPendingNews"
                :pending-news="pendingNews"
                :loading-maviepro-news="loadingMavieproNews"
                :maviepro-news="mavieproNews"
                :total-pending="totalPendingNewsCount"
                :can-create="hasNewsEditionRights"
                :is-opening="isOpeningArticleModal"
                :duplicating="duplicatingMavieproArticle"
                :format-number="formatNumber"
                :format-date="formatPublicationDate"
                :show-inactive="true"
                :show-maviepro="true"
                :show-create-button="false"
                @toggle="toggleLine('news')"
                @create="createArticle"
                @open="openArticle"
                @duplicate="duplicateMavieproArticle"
              />
            </div>

            <div class="relative">
              <dashboard-row-toggle
                :collapsed="collapsedLines.surveys"
                @toggle="toggleLine('surveys')"
              />

              <dashboard-pending-surveys-card
                :collapsed="collapsedLines.surveys"
                title-override="Sondages à publier"
                card-class="dashboard-action-card"
                :loading-pending="loadingPendingSurveys"
                :pending-surveys="pendingSurveys"
                :loading-maviepro="loadingMavieproSurveys"
                :maviepro-surveys="mavieproSurveys"
                :can-create="hasSurveyEditionRights"
                :is-opening="isOpeningSurveyModal"
                :duplicating="duplicatingMavieproSurvey"
                :total-pending="totalPendingSurveysCount"
                :format-number="formatNumber"
                :format-date="formatPublicationDate"
                :show-create-button="false"
                @toggle="toggleLine('surveys')"
                @create="createSurvey"
                @activate="activatePendingSurvey"
                @duplicate="duplicateMavieproSurvey"
              />
            </div>
          </div>
        </section>
      </section>
    </page-content>

    <div
      v-if="isMonthlyTrackingOpen"
      class="dashboard-modal-overlay"
      @click.self="closeMonthlyTracking"
    >
      <div class="dashboard-modal-card dashboard-modal-card--wide">
        <header class="dashboard-modal-header">
          <div>
            <h3 class="dashboard-modal-title">Suivi mois par mois</h3>
            <p class="dashboard-modal-subtitle">
              Téléchargements, utilisateurs actifs, lectures et publications sur l'année sélectionnée.
            </p>
          </div>
          <button
            type="button"
            class="dashboard-modal-close"
            aria-label="Fermer le suivi mois par mois"
            @click="closeMonthlyTracking"
          >
            ×
          </button>
        </header>

        <div class="dashboard-modal-table-wrapper dashboard-monthly-table-wrapper">
          <table class="dashboard-modal-table dashboard-monthly-table">
            <thead>
              <tr>
                <th>
                  <select v-model.number="selectedTrackingYear" class="dashboard-year-select">
                    <option v-for="year in trackingYearOptions" :key="year" :value="year">
                      {{ year }}
                    </option>
                  </select>
                </th>
                <th v-for="month in monthlyTrackingRows" :key="month.key" class="is-right">
                  {{ month.label }}
                </th>
              </tr>
            </thead>
            <tbody v-if="!isMonthlyTrackingLoading">
              <tr v-for="row in monthlyTrackingTableRows" :key="row.key">
                <th>{{ row.label }}</th>
                <td v-for="month in monthlyTrackingRows" :key="`${row.key}-${month.key}`" class="is-right">
                  {{ formatMonthlyValue(month[row.key], row.format) }}
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="isMonthlyTrackingLoading" class="dashboard-monthly-loader" aria-live="polite">
            <span class="dashboard-monthly-spinner" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from 'lodash'
import PageContent from '../components/PageContent.vue'
import DashboardHeader from './dashboard/components/DashboardHeader.vue'
import DashboardDownloadsCard from '@/components/dashboard/DashboardDownloadsCard.vue'
import DashboardPendingNewsCard from '@/components/dashboard/DashboardPendingNewsCard.vue'
import DashboardPendingSurveysCard from '@/components/dashboard/DashboardPendingSurveysCard.vue'
import DashboardLoginsCard from '@/components/dashboard/DashboardLoginsCard.vue'
import DashboardActiveSurveysCard from '@/components/dashboard/DashboardActiveSurveysCard.vue'
import DashboardTopKeywordsCard from '@/components/dashboard/DashboardTopKeywordsCard.vue'
import DashboardReadsCard from '@/components/dashboard/DashboardReadsCard.vue'
import DashboardRowToggle from '@/components/dashboard/DashboardRowToggle.vue'
import dashboardComputed from './dashboard/computed'
import dashboardWatchers from './dashboard/watchers'
import dashboardMethods from './dashboard/methods'
import { createDashboardState } from './dashboard/state'

export default {
  components: {
    PageContent,
    DashboardHeader,
    DashboardDownloadsCard,
    DashboardPendingNewsCard,
    DashboardPendingSurveysCard,
    DashboardLoginsCard,
    DashboardActiveSurveysCard,
    DashboardTopKeywordsCard,
    DashboardReadsCard,
    DashboardRowToggle
  },
  data () {
    return createDashboardState()
  },
  created () {
    this.debounceUpdateRequest = debounce(this.updateRequest, 600)
    this.syncQuickRange()
    this.initializeCardOrders()
    if (this.domain && this.domain.uuid) {
      this.onDomainChange(this.domain, null, { force: true })
    }
  },
  computed: {
    ...mapGetters(['domain', 'user', 'roles', 'application']),
    ...dashboardComputed,
    shouldDisplayActiveSurveysCard () {
      if (this.loadingActiveSurveys) return true
      if (Array.isArray(this.activeSurveys) && this.activeSurveys.length > 0) return true
      return false
    },
    periodLabel () {
      const r = Array.isArray(this.dateRange) ? this.dateRange : null
      const [start, end] = r && r.length === 2 ? r : [this.dateStart, this.dateEnd]
      const fmt = (val) => {
        if (!val || typeof val !== 'string' || val.length < 10) return ''
        // yyyy-MM-dd -> dd/MM/yyyy
        const y = val.slice(0, 4)
        const m = val.slice(5, 7)
        const d = val.slice(8, 10)
        return `${d}/${m}/${y}`
      }
      const a = fmt(start)
      const b = fmt(end)
      if (a && b) return `${a} – ${b}`
      return ''
    },
    trackingYearOptions () {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let year = currentYear; year >= 2020; year--) {
        years.push(year)
      }
      return years
    },
    monthlyTrackingRows () {
      const monthLabels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
      const rows = Array.isArray(this.monthlyTrackingSeries) ? this.monthlyTrackingSeries : []
      return monthLabels.map((label, index) => {
        const monthNumber = index + 1
        const row = rows.find(item => Number(item.month) === monthNumber) || {}
        const isFutureMonth = this.isFutureTrackingMonth(monthNumber)

        return {
          key: monthNumber,
          label,
          downloads: Number(row.downloads || 0),
          downloadsCumulative: isFutureMonth ? 0 : Number(row.downloadsCumulative || 0),
          downloadRateWorkforce: isFutureMonth ? 0 : Number(row.downloadRateWorkforce || 0),
          downloadRateAverage: isFutureMonth ? 0 : Number(row.downloadRateAverage || 0),
          activeLogins: Number(row.activeLogins || 0),
          activeUsers: Number(row.activeUsers || 0),
          activeUsersRateDownloads: isFutureMonth ? 0 : Number(row.activeUsersRateDownloads || 0),
          activeUsersRateAverage: isFutureMonth ? 0 : Number(row.activeUsersRateAverage || 0),
          allReads: isFutureMonth ? 0 : Number(row.allReads || 0),
          articleReads: isFutureMonth ? 0 : Number(row.articleReads || 0),
          faqReads: isFutureMonth ? 0 : Number(row.faqReads || 0),
          publishedNews: isFutureMonth ? 0 : Number(row.publishedNews || 0)
        }
      })
    },
    monthlyTrackingTableRows () {
      return [
        { key: 'downloadsCumulative', label: 'Total DL', format: 'number' },
        { key: 'downloads', label: 'Evolution DL', format: 'number' },
        { key: 'downloadRateWorkforce', label: '% DL (effectif)', format: 'percent' },
        { key: 'activeLogins', label: 'Total connexion', format: 'number' },
        { key: 'activeUsers', label: 'Total UA', format: 'number' },
        { key: 'activeUsersRateDownloads', label: '% UA unique (DL)', format: 'percent' },
        { key: 'allReads', label: 'Lecture all', format: 'number' },
        { key: 'articleReads', label: 'Lectures articles', format: 'number' },
        { key: 'faqReads', label: 'Lectures FAQ', format: 'number' },
        { key: 'publishedNews', label: 'Nombre d’actualités publiées', format: 'number' }
      ]
    }
  },
  watch: {
    ...dashboardWatchers,
    selectedTrackingYear () {
      if (this.isMonthlyTrackingOpen) {
        this.loadMonthlyTracking()
      }
    }
  },
  methods: {
    ...dashboardMethods
  }
}
</script>

<style src="./Dashboard.scss" lang="scss"></style>
